import {
  AUTH_ACTIONS_CLASS,
  AUTH_CONTAINER_CLASS,
  AUTH_FIELDS_CLASS,
  AuthPage,
  AuthPrimaryButton,
} from "@/components/auth";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { Button, Checkbox, RouteLink, useToast } from "@/components/ui";
import { legalService, TPendingLegalDocument } from "@/api/legal";
import { userService } from "@/api/user/user.service";
import { useRouter, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

import { useStore } from "@/store";
import { getPostLoginRedirectPath } from "@/utils/authUtils";
import { hasAdminRole } from "@/utils/routeGuards";
import { extractErrorMessage } from "@/utils/errorUtils";

export const AcceptLegal = () => {
  const { toast } = useToast();
  const router = useRouter();
  const { from: returnPath } = useSearch({ from: "/auth/accept-legal" });
  const roles = useStore.use.roles();

  const [pending, setPending] = useState<TPendingLegalDocument[]>([]);
  const [acceptedKeys, setAcceptedKeys] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const { data, error } = await legalService.getStatus();
    if (error) {
      setLoadError(await extractErrorMessage(error));
      setIsLoading(false);
      return;
    }

      if (data?.data.all_required_accepted) {
        const redirectPath = getPostLoginRedirectPath({
          from: returnPath,
          isAdmin: hasAdminRole(roles),
        });
        window.location.assign(redirectPath);
        return;
      }

      setPending(data?.data.pending ?? []);
      setIsLoading(false);
  }, [returnPath, roles]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const allChecked = useMemo(
    () => pending.length > 0 && pending.every((doc) => acceptedKeys[doc.document_type]),
    [pending, acceptedKeys]
  );

  const handleSubmit = useCallback(async () => {
    if (!allChecked) {
      toast({
        variant: "destructive",
        title: "Acceptance required",
        description: "Please accept all listed documents to continue.",
      });
      return;
    }

    setIsSubmitting(true);
    const { data, error } = await legalService.accept({
      acceptances: pending.map((doc) => ({
        document_type: doc.document_type,
        version: doc.version,
      })),
    });
    setIsSubmitting(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not record acceptance",
        description: await extractErrorMessage(error),
      });
      return;
    }

    if (data) {
      toast({
        variant: "success",
        title: "Legal documents accepted",
        description: "You can now continue using Fundy.",
      });

      let isAdmin = hasAdminRole(roles);
      if (!roles) {
        const { data: rolesData } = await userService.getRoles();
        isAdmin = hasAdminRole(rolesData);
      }

      const redirectPath = getPostLoginRedirectPath({ from: returnPath, isAdmin });
      await router.invalidate().finally(() => {
        window.location.assign(redirectPath);
      });
    }
  }, [allChecked, pending, returnPath, roles, router, toast]);

  const documentLink = (documentType: string) => {
    if (documentType === "privacy_policy") return "/privacy";
    if (documentType === "cookies_policy") return "/cookies-policy";
    return "/terms";
  };

  return (
    <div className={AUTH_CONTAINER_CLASS}>
      <AuthPage
        title="Accept updated legal documents"
        subtitle="We've updated our legal documents. Please review and accept the following to continue using Fundy."
      >
        <div className={AUTH_FIELDS_CLASS}>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : loadError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-center space-y-3">
              <div className="flex items-center justify-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm font-medium">{loadError}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void loadStatus()}
                className="mx-auto flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {pending.map((doc) => (
                <label
                  key={doc.document_id}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-4"
                >
                  <Checkbox
                    checked={Boolean(acceptedKeys[doc.document_type])}
                    onCheckedChange={(checked) =>
                      setAcceptedKeys((prev) => ({
                        ...prev,
                        [doc.document_type]: checked === true,
                      }))
                    }
                  />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{doc.title}</p>
                    <p className="text-xs text-muted-foreground">Version {doc.version}</p>
                    <RouteLink
                      to={documentLink(doc.document_type)}
                      className="text-sm text-primary underline"
                      target="_blank"
                    >
                      Read full document
                    </RouteLink>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className={AUTH_ACTIONS_CLASS}>
            <AuthPrimaryButton disabled={isLoading || isSubmitting || !allChecked} isLoading={isSubmitting} onClick={() => void handleSubmit()}>
              Accept and continue
            </AuthPrimaryButton>
          </div>
        </div>
      </AuthPage>
    </div>
  );
};
